import { useAuth } from "@clerk/tanstack-react-start"
import * as Sentry from "@sentry/react"
import { api } from "@stoneflow/backend/convex/_generated/api"
import type { Id } from "@stoneflow/backend/convex/_generated/dataModel"
import {
	ALLOWED_ATTACHMENT_MIME_TYPES,
	MAX_ATTACHMENTS_PER_EXCEPTION,
	MAX_ATTACHMENT_SIZE_BYTES,
	MAX_TOTAL_BYTES_PER_EXCEPTION,
	isAllowedMimeType,
	mimeFromFileName,
} from "@stoneflow/shared/validators/attachment"
import { createFileRoute } from "@tanstack/react-router"
import { useMutation, useQuery } from "convex/react"
import { Paperclip, X } from "lucide-react"
import { useEffect, useMemo, useRef, useState } from "react"
import { toast } from "sonner"
import { AppLink } from "#/components/app-link"
import type { AutocompleteSuggestion, FieldDefinition } from "#/components/dynamic-field"
import { DynamicForm } from "#/components/dynamic-form"
import { ParentExceptionPicker } from "#/components/parent-exception-picker"
import { RouteError } from "#/components/route-error"
import { Button, buttonVariants } from "#/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "#/components/ui/card"
import { Label } from "#/components/ui/label"
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "#/components/ui/select"
import { useAppNavigate } from "#/hooks/use-app-navigate"
import { useLogRole } from "#/hooks/useLogRole"
import { useOrgMembers, withYouSuffix } from "#/hooks/useOrgMembers"
import { isFieldVisible } from "#/lib/field-conditional"
import { trackEvent } from "#/lib/posthog"
import { useStonelog } from "#/lib/stonelog-context"

export const Route = createFileRoute("/_authenticated/stonelog/exceptions/new")({
	errorComponent: RouteError,
	validateSearch: (search: Record<string, unknown>) => ({
		parentId: typeof search.parentId === "string" ? search.parentId : undefined,
	}),
	component: NewException,
})

type StagedFile = { file: File; effectiveType: string; id: string }

function NewException() {
	const navigate = useAppNavigate()
	const search = Route.useSearch()
	const { currentLogId, currentLog } = useStonelog()
	const { atLeast, isLoading: capLoading } = useLogRole(currentLogId ?? undefined)
	const hasCapability = atLeast("editor")
	const { members, currentUserId } = useOrgMembers()
	const { getToken } = useAuth()

	const [selectedCategoryId, setSelectedCategoryId] = useState<Id<"categories"> | null>(null)
	const [selectedTypeId, setSelectedTypeId] = useState<Id<"exceptionTypes"> | null>(null)
	const [formValues, setFormValues] = useState<Record<string, unknown>>({})
	const [formErrors, setFormErrors] = useState<Record<string, string>>({})
	const [assignedDeptId, setAssignedDeptId] = useState<Id<"departments"> | null>(null)
	const [assignedUserId, setAssignedUserId] = useState("")
	// Once the user edits the department by hand, stop auto-deriving it from the assignee.
	const deptManuallySet = useRef(false)
	const [parentId, setParentId] = useState<Id<"exceptions"> | null>(
		(search.parentId as Id<"exceptions"> | undefined) ?? null,
	)
	const [isSubmitting, setIsSubmitting] = useState(false)
	const [stagedFiles, setStagedFiles] = useState<StagedFile[]>([])
	const [uploadPhase, setUploadPhase] = useState<"idle" | "uploading">("idle")
	const [isDragging, setIsDragging] = useState(false)
	const attachmentInputRef = useRef<HTMLInputElement>(null)
	const defaultsApplied = useRef(false)

	useEffect(() => {
		if (!currentLogId) navigate({ to: "/stonelog/select-log" })
	}, [currentLogId, navigate])

	const logTypeId = currentLog?.logTypeId

	const fieldSchema = useQuery(
		api.stonelog.fieldSchemas.getCurrent,
		logTypeId ? { logTypeId } : "skip",
	)
	const categories = useQuery(
		api.stonelog.categories.listByLogType,
		logTypeId ? { logTypeId } : "skip",
	)
	const exceptionTypes = useQuery(
		api.stonelog.exceptionTypes.listByCategory,
		selectedCategoryId ? { categoryId: selectedCategoryId } : "skip",
	)
	const departments = useQuery(
		api.stonelog.departments.listForLog,
		currentLogId ? { logId: currentLogId } : "skip",
	)
	const userDepartments = useQuery(
		api.stonelog.departments.listUserDepartments,
		currentLogId ? {} : "skip",
	)
	const parts = useQuery(api.stonelog.parts.list, currentLogId ? {} : "skip")
	const suppliers = useQuery(api.stonelog.suppliers.list, currentLogId ? {} : "skip")

	const create = useMutation(api.stonelog.exceptions.create)

	// Auto-select single category
	useEffect(() => {
		if (categories?.length === 1 && !selectedCategoryId) {
			setSelectedCategoryId(categories[0]._id)
		}
	}, [categories, selectedCategoryId])

	// Apply defaults when schema loads
	useEffect(() => {
		if (fieldSchema?.fields && !defaultsApplied.current) {
			defaultsApplied.current = true
			const defaults: Record<string, unknown> = {}
			for (const f of fieldSchema.fields as FieldDefinition[]) {
				if (f.defaultValue !== undefined) defaults[f.id] = f.defaultValue
			}
			setFormValues(defaults)
		}
	}, [fieldSchema])

	// Map of bare Clerk userId → their department, for deriving the exception's department.
	const deptByUser = useMemo(() => {
		const m = new Map<string, Id<"departments">>()
		for (const ud of userDepartments ?? []) m.set(ud.userId, ud.departmentId)
		return m
	}, [userDepartments])

	// Derive the department from the assignee until the user overrides it. Only apply a derived
	// department when it's actually offered for this log.
	useEffect(() => {
		if (deptManuallySet.current) return
		if (!assignedUserId) {
			setAssignedDeptId(null)
			return
		}
		const userDept = deptByUser.get(assignedUserId)
		if (userDept && departments?.some((d) => d._id === userDept)) {
			setAssignedDeptId(userDept)
		} else {
			setAssignedDeptId(null)
		}
	}, [assignedUserId, deptByUser, departments])

	function handleCategoryChange(id: string) {
		setSelectedCategoryId(id as Id<"categories">)
		setSelectedTypeId(null)
	}

	function handleFieldChange(fieldId: string, value: unknown) {
		setFormValues((prev) => ({ ...prev, [fieldId]: value }))
		if (formErrors[fieldId]) {
			setFormErrors((prev) => {
				const next = { ...prev }
				delete next[fieldId]
				return next
			})
		}
	}

	function handleAutocompleteSelect(fieldId: string, item: AutocompleteSuggestion) {
		if (fieldId === "part_number") {
			const part = parts?.find((p) => p.partNumber === item.value)
			if (part) setFormValues((prev) => ({ ...prev, part_description: part.description }))
		} else if (fieldId === "part_description") {
			const part = parts?.find((p) => p.description === item.value)
			if (part) setFormValues((prev) => ({ ...prev, part_number: part.partNumber }))
		} else if (fieldId === "supplier_name") {
			const s = suppliers?.find((s) => s.name === item.value)
			if (s?.supplierNumber)
				setFormValues((prev) => ({ ...prev, supplier_number: s.supplierNumber }))
		}
	}

	const suggestions: Record<string, AutocompleteSuggestion[]> = {}
	if (parts) {
		suggestions.part_number = parts.map((p) => ({ label: p.partNumber, value: p.partNumber }))
		suggestions.part_description = parts.map((p) => ({
			label: p.description,
			value: p.description,
		}))
	}
	if (suppliers) {
		suggestions.supplier_name = suppliers.map((s) => ({ label: s.name, value: s.name }))
	}

	function addStagedFiles(files: FileList | null) {
		if (!files || files.length === 0) return
		const accepted: StagedFile[] = []
		let runningCount = stagedFiles.length
		let runningBytes = stagedFiles.reduce((sum, s) => sum + s.file.size, 0)
		for (const file of Array.from(files)) {
			const effectiveType = isAllowedMimeType(file.type)
				? file.type
				: (mimeFromFileName(file.name) ?? file.type)
			if (!isAllowedMimeType(effectiveType)) {
				toast.error(`${file.name}: unsupported file type. Allowed: PDF, PNG, JPG`)
				continue
			}
			if (file.size <= 0) {
				toast.error(`${file.name}: file is empty`)
				continue
			}
			if (file.size > MAX_ATTACHMENT_SIZE_BYTES) {
				toast.error(
					`${file.name}: too large. Max ${Math.floor(
						MAX_ATTACHMENT_SIZE_BYTES / 1024 / 1024,
					)} MB per file`,
				)
				continue
			}
			if (runningCount + 1 > MAX_ATTACHMENTS_PER_EXCEPTION) {
				toast.error(`Max ${MAX_ATTACHMENTS_PER_EXCEPTION} attachments per exception`)
				break
			}
			if (runningBytes + file.size > MAX_TOTAL_BYTES_PER_EXCEPTION) {
				toast.error(
					`Combined attachments exceed ${Math.floor(
						MAX_TOTAL_BYTES_PER_EXCEPTION / 1024 / 1024,
					)} MB limit`,
				)
				break
			}
			runningCount += 1
			runningBytes += file.size
			accepted.push({
				file,
				effectiveType,
				id: `${file.name}-${file.size}-${file.lastModified}-${accepted.length}`,
			})
		}
		if (accepted.length > 0) setStagedFiles((prev) => [...prev, ...accepted])
	}

	function removeStagedFile(id: string) {
		setStagedFiles((prev) => prev.filter((s) => s.id !== id))
	}

	async function uploadStaged(exceptionId: Id<"exceptions">) {
		if (stagedFiles.length === 0) return
		const token = await getToken({ template: "convex" })
		if (!token) {
			toast.error("Could not upload attachments: not authenticated")
			return
		}
		setUploadPhase("uploading")
		let succeeded = 0
		for (const staged of stagedFiles) {
			const uploadFile =
				staged.effectiveType === staged.file.type
					? staged.file
					: new File([staged.file], staged.file.name, { type: staged.effectiveType })
			const form = new FormData()
			form.append("file", uploadFile)
			try {
				const res = await fetch(`/api/stonelog/attachments/upload?exceptionId=${exceptionId}`, {
					method: "POST",
					headers: { Authorization: `Bearer ${token}` },
					body: form,
				})
				if (!res.ok) {
					let message = "Upload failed"
					try {
						const body = (await res.json()) as { error?: string }
						if (body.error) message = body.error
					} catch {
						// ignore
					}
					toast.error(`${staged.file.name}: ${message}`)
				} else {
					succeeded += 1
				}
			} catch (err) {
				Sentry.captureException(err)
				toast.error(`${staged.file.name}: network error during upload`)
			}
		}
		if (succeeded > 0) {
			toast.success(
				succeeded === stagedFiles.length
					? `Uploaded ${succeeded} attachment${succeeded === 1 ? "" : "s"}`
					: `Uploaded ${succeeded} of ${stagedFiles.length} attachments`,
			)
		}
		setUploadPhase("idle")
	}

	function validate(fields: FieldDefinition[]): boolean {
		const errors: Record<string, string> = {}
		const selectedTypeName = (exceptionTypes ?? []).find((t) => t._id === selectedTypeId)?.name
		const condCtx = { values: formValues, exceptionTypeName: selectedTypeName }
		for (const f of fields) {
			if (!isFieldVisible(f, condCtx)) continue
			const val = formValues[f.id]
			if (f.validation?.required && (val == null || val === "")) {
				errors[f.id] = f.validation.message ?? `${f.label} is required`
			}
			if (f.type === "number" && val !== undefined && val !== "") {
				const num = Number(val)
				if (f.validation?.min !== undefined && num < f.validation.min)
					errors[f.id] = `Minimum value is ${f.validation.min}`
				if (f.validation?.max !== undefined && num > f.validation.max)
					errors[f.id] = `Maximum value is ${f.validation.max}`
			}
			if (f.validation?.pattern && typeof val === "string" && val !== "") {
				if (!new RegExp(f.validation.pattern).test(val)) {
					errors[f.id] = f.validation.message ?? `${f.label} has invalid format`
				}
			}
		}
		setFormErrors(errors)
		return Object.keys(errors).length === 0
	}

	async function handleSubmit(e: React.FormEvent) {
		e.preventDefault()
		if (!currentLogId || !currentLog || !selectedCategoryId || !selectedTypeId) return

		const fields = (fieldSchema?.fields ?? []) as FieldDefinition[]
		if (!validate(fields)) return

		setIsSubmitting(true)
		try {
			const newExceptionId = await create({
				logId: currentLogId,
				logTypeId: currentLog.logTypeId,
				categoryId: selectedCategoryId,
				typeId: selectedTypeId,
				description: String(formValues.description ?? ""),
				priority: String(formValues.priority ?? "medium") as "low" | "medium" | "high" | "critical",
				dynamicFields: formValues,
				assignedDepartmentId: assignedDeptId ?? undefined,
				assignedUserId: assignedUserId || undefined,
				parentId: parentId ?? undefined,
			})
			trackEvent("stonelog_exception_created", {
				log_id: currentLogId,
				category_id: selectedCategoryId,
				type_id: selectedTypeId,
				priority: String(formValues.priority ?? "medium"),
			})
			toast.success("Exception created")
			await uploadStaged(newExceptionId)
			navigate({ to: "/stonelog/exceptions" })
		} catch (err) {
			Sentry.captureException(err)
			toast.error(err instanceof Error ? err.message : "Failed to create exception")
		} finally {
			setIsSubmitting(false)
		}
	}

	if (!capLoading && !hasCapability) {
		navigate({ to: "/stonelog/exceptions" })
		return null
	}

	if (!currentLogId) return null

	const showCategorySelector = (categories?.length ?? 0) > 1
	const fields = (fieldSchema?.fields ?? []) as FieldDefinition[]
	const selectedTypeName = (exceptionTypes ?? []).find((t) => t._id === selectedTypeId)?.name

	return (
		<div className="mx-auto max-w-3xl space-y-6 p-6">
			<div className="flex items-center gap-2">
				<AppLink
					to="/stonelog/exceptions"
					className="text-sm text-foreground-muted hover:text-foreground"
				>
					Exceptions
				</AppLink>
				<span className="text-foreground-muted">/</span>
				<span className="text-sm font-medium">New Exception</span>
			</div>

			<h1>New Exception</h1>

			<form onSubmit={handleSubmit} className="space-y-6">
				<Card>
					<CardHeader>
						<CardTitle>Exception Type</CardTitle>
					</CardHeader>
					<CardContent className="grid grid-cols-2 gap-4">
						{showCategorySelector && (
							<div className="space-y-1">
								<Label>Category</Label>
								<Select value={selectedCategoryId ?? ""} onValueChange={handleCategoryChange}>
									<SelectTrigger className="w-full">
										<SelectValue placeholder="Select category" aria-label="Category">
											{(categories ?? []).find((c) => c._id === selectedCategoryId)?.name}
										</SelectValue>
									</SelectTrigger>
									<SelectContent>
										{(categories ?? []).map((c) => (
											<SelectItem key={c._id} value={c._id}>
												{c.name}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							</div>
						)}
						<div className={showCategorySelector ? "space-y-1" : "col-span-2 space-y-1"}>
							<Label>Exception Type</Label>
							<Select
								value={selectedTypeId ?? ""}
								onValueChange={(v) => setSelectedTypeId(v as Id<"exceptionTypes">)}
								disabled={!selectedCategoryId}
							>
								<SelectTrigger className="w-full">
									<SelectValue placeholder="Select type" aria-label="Exception type">
										{(exceptionTypes ?? []).find((t) => t._id === selectedTypeId)?.name}
									</SelectValue>
								</SelectTrigger>
								<SelectContent>
									{(exceptionTypes ?? []).map((t) => (
										<SelectItem key={t._id} value={t._id}>
											{t.name}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>
					</CardContent>
				</Card>

				{selectedTypeId && fields.length > 0 && (
					<Card>
						<CardHeader>
							<CardTitle>Details</CardTitle>
						</CardHeader>
						<CardContent>
							<DynamicForm
								fields={fields}
								values={formValues}
								onChange={handleFieldChange}
								errors={formErrors}
								suggestions={suggestions}
								onAutocompleteSelect={handleAutocompleteSelect}
								exceptionTypeName={selectedTypeName}
							/>
						</CardContent>
					</Card>
				)}

				<Card>
					<CardHeader>
						<CardTitle>Link to Parent</CardTitle>
					</CardHeader>
					<CardContent>
						<ParentExceptionPicker
							logId={currentLogId}
							value={parentId}
							onChange={(id) => setParentId(id)}
						/>
					</CardContent>
				</Card>

				<Card>
					<CardHeader>
						<CardTitle>Attachments</CardTitle>
					</CardHeader>
					<CardContent className="space-y-3">
						<input
							ref={attachmentInputRef}
							type="file"
							accept={ALLOWED_ATTACHMENT_MIME_TYPES.join(",")}
							multiple
							className="hidden"
							onChange={(e) => {
								addStagedFiles(e.target.files)
								e.target.value = ""
							}}
						/>
						<button
							type="button"
							onClick={() => attachmentInputRef.current?.click()}
							onDragEnter={(e) => {
								e.preventDefault()
								e.stopPropagation()
								if (e.dataTransfer.types.includes("Files")) setIsDragging(true)
							}}
							onDragOver={(e) => {
								e.preventDefault()
								e.stopPropagation()
								e.dataTransfer.dropEffect = "copy"
							}}
							onDragLeave={(e) => {
								e.preventDefault()
								e.stopPropagation()
								if (e.currentTarget.contains(e.relatedTarget as Node | null)) return
								setIsDragging(false)
							}}
							onDrop={(e) => {
								e.preventDefault()
								e.stopPropagation()
								setIsDragging(false)
								addStagedFiles(e.dataTransfer.files)
							}}
							className={`w-full rounded-md border border-dashed p-4 text-left transition-colors ${
								isDragging
									? "border-foreground/60 bg-accent"
									: "border-border bg-background hover:border-foreground/30"
							}`}
						>
							<div className="flex items-center gap-3">
								<Paperclip className="size-4 shrink-0 text-foreground-muted" />
								<div className="flex-1 text-sm">
									<p className="font-medium text-foreground">Add evidence</p>
									<p className="text-xs text-foreground-muted">
										PDF, PNG or JPG. Max {Math.floor(MAX_ATTACHMENT_SIZE_BYTES / 1024 / 1024)} MB
										per file, {MAX_ATTACHMENTS_PER_EXCEPTION} files total. Uploads start after the
										exception is created.
									</p>
								</div>
								<span className="inline-flex h-8 items-center rounded-md border border-foreground/20 px-3 text-xs font-medium text-foreground">
									Browse
								</span>
							</div>
						</button>
						{stagedFiles.length > 0 && (
							<ul className="space-y-1">
								{stagedFiles.map((s) => (
									<li
										key={s.id}
										className="flex items-center gap-3 rounded-md border border-border bg-background px-3 py-2 text-sm"
									>
										<Paperclip className="size-4 shrink-0 text-foreground-muted" />
										<span className="flex-1 truncate">{s.file.name}</span>
										<span className="text-xs text-foreground-muted">
											{(s.file.size / 1024 / 1024).toFixed(2)} MB
										</span>
										<button
											type="button"
											onClick={() => removeStagedFile(s.id)}
											className="rounded-md p-1 text-foreground-muted hover:bg-accent hover:text-foreground"
											aria-label={`Remove ${s.file.name}`}
										>
											<X className="size-4" />
										</button>
									</li>
								))}
							</ul>
						)}
					</CardContent>
				</Card>

				{((departments && departments.length > 0) || members.length > 0) && (
					<Card>
						<CardHeader>
							<CardTitle>Assignment</CardTitle>
						</CardHeader>
						<CardContent className="space-y-4">
							{departments && departments.length > 0 && (
								<div className="space-y-1">
									<Label>Department (optional)</Label>
									<Select
										value={assignedDeptId ?? ""}
										onValueChange={(v) => {
											deptManuallySet.current = true
											setAssignedDeptId(v ? (v as Id<"departments">) : null)
										}}
									>
										<SelectTrigger>
											<SelectValue placeholder="Unassigned" aria-label="Department">
												{assignedDeptId
													? (departments?.find((d) => d._id === assignedDeptId)?.name ??
														"Unassigned")
													: undefined}
											</SelectValue>
										</SelectTrigger>
										<SelectContent>
											<SelectItem value="">Unassigned</SelectItem>
											{departments.map((d) => (
												<SelectItem key={d._id} value={d._id}>
													{d.name}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
								</div>
							)}
							{members.length > 0 && (
								<div className="space-y-1">
									<Label>Assign to User (optional)</Label>
									<Select value={assignedUserId} onValueChange={setAssignedUserId}>
										<SelectTrigger>
											<SelectValue placeholder="Unassigned" aria-label="Assigned user">
												{assignedUserId
													? withYouSuffix(
															members.find((m) => m.userId === assignedUserId)?.name ??
																"Unassigned",
															assignedUserId,
															currentUserId,
														)
													: undefined}
											</SelectValue>
										</SelectTrigger>
										<SelectContent>
											<SelectItem value="">Unassigned</SelectItem>
											{members.map((m) => (
												<SelectItem key={m.userId} value={m.userId}>
													{withYouSuffix(m.name, m.userId, currentUserId)}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
								</div>
							)}
						</CardContent>
					</Card>
				)}

				<div className="flex items-center gap-3">
					<Button type="submit" disabled={!selectedTypeId || isSubmitting}>
						{uploadPhase === "uploading"
							? "Uploading attachments..."
							: isSubmitting
								? "Creating..."
								: "Create Exception"}
					</Button>
					<AppLink to="/stonelog/exceptions" className={buttonVariants({ variant: "outline" })}>
						Cancel
					</AppLink>
				</div>
			</form>
		</div>
	)
}
